import { createUnvalidatedValidationSummary, type CoasterProject } from '../schema';
import { createRideCriticalFingerprint, isSuccessfulTestCurrent } from '../ride/certification.ts';
import {
  liftDropCoasterFixture,
  loopworksShowcaseFixture,
  ovalCoasterFixture,
  systemsCircuitCoasterFixture,
} from '../fixtures';
import type { CoasterPlatformAdapter, FixtureLaunchRequest, LaunchResolution } from './adapter';
import type {
  AuthenticatedViewer,
  CatalogHoverUploadDescriptor,
  CatalogHoverVideoAsset,
  CloudDraftRecord,
  CloudDraftSummary,
  HeroUploadDescriptor,
  HeroAssetBundle,
  MyCoasterSummary,
  OwnerProjectState,
  PublicCatalogPage,
  PublicCatalogQuery,
  PublicCoasterCardData,
  PublicCoasterPayload,
  PublicCoasterStats,
  PublicCoasterRecord,
  PublicCoasterViewerStateBatch,
  PublishRequest,
  PublishStatusRecord,
  ReadOnlyShare,
  ReadOnlyShareRecord,
  RemixRequest,
  RemixResult,
  ReferralLinkResult,
  QualifiedRideCompletionRequest,
  QualifiedRideCompletionResult,
  QualifiedRideStartRequest,
  QualifiedRideStartResult,
  SaveCloudDraftOptions,
  SocialMutationRequest,
  SocialMutationResult,
  StorageLike,
} from './contracts';
import { hydratePublicCoaster } from './public-model';
import {
  type PlatformCapabilityId,
  type PlatformCapabilityState,
  type PlatformErrorCode,
  type PlatformResult,
  unavailable,
} from './capabilities';

export const PHASE4_QA_IDENTITIES = {
  'qa.alice': {
    kind: 'authenticated',
    id: 'qa.alice',
    displayName: 'Alice Builder',
    avatarUrl: null,
  },
  'qa.bob': { kind: 'authenticated', id: 'qa.bob', displayName: 'Bob Rider', avatarUrl: null },
} as const satisfies Record<string, AuthenticatedViewer>;

export type Phase4QaIdentityId = keyof typeof PHASE4_QA_IDENTITIES;

interface StoredRevision {
  revisionId: string;
  projectId: string;
  project: CoasterProject;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  createdAt: string;
  publishedAt: string | null;
  catalogHoverUpload: CatalogHoverUploadDescriptor | null;
}

interface StoredProject {
  ownerId: string;
  version: number;
  project: CoasterProject;
  lifecycle: 'active' | 'archived';
  visibility: 'private' | 'public' | 'hidden';
  workingRevisionStatus: 'not_submitted' | 'pending' | 'approved' | 'rejected';
  pendingRevisionId: string | null;
  lastRejectedRevisionId: string | null;
  currentPublicRevisionId: string | null;
  publicCoasterId: string | null;
  publicSlug: string | null;
  rejectionReason: string | null;
  updatedAt: string;
}

interface StoredShare {
  shareId: string;
  ownerId: string;
  projectId: string;
  revisionId: string;
  secretHash: string;
  project: CoasterProject;
  createdAt: string;
}

interface Phase4QaState {
  schemaVersion: 3;
  projects: Record<string, StoredProject>;
  revisions: Record<string, StoredRevision>;
  shares: Record<string, StoredShare>;
  social: Record<
    string,
    {
      boostedBy: string[];
      favoritedBy: string[];
      qualifiedRides: number;
      remixes: number;
      referredRides: number;
      idempotency: Record<string, true>;
    }
  >;
  referrals: Record<
    string,
    {
      publicCoasterId: string;
      createdBy: string;
      createdAt: string;
      expiresAt: string;
      completedRideSessions: string[];
    }
  >;
  rideSessions: Record<
    string,
    {
      publicCoasterId: string;
      referralId: string | null;
      startedAt: string;
      completedAt: string | null;
      qualified: boolean;
      counted: boolean;
    }
  >;
}

export interface LocalPhase4QaPlatformAdapterOptions {
  storage: StorageLike;
  identity?: Phase4QaIdentityId;
  storageKey?: string;
  now?: () => string;
  idFactory?: (prefix: string) => string;
}

const qaStates = {
  anonymous_rides: {
    id: 'anonymous_rides',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Anonymous rides are available.',
  },
  fixture_launch: {
    id: 'fixture_launch',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Fixture launch IDs are available.',
  },
  canvas_capture: {
    id: 'canvas_capture',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Canvas capture is available.',
  },
  cloud_drafts: {
    id: 'cloud_drafts',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA cloud-draft emulation is available.',
  },
  publishing: {
    id: 'publishing',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA moderation emulation is available.',
  },
  catalog_hover_upload: {
    id: 'catalog_hover_upload',
    availability: 'available',
    phaseGate: 'phase_8',
    reason: 'Local QA Buncha Games motion preview upload emulation is available.',
  },
  public_catalog: {
    id: 'public_catalog',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA public records are available.',
  },
  read_only_shares: {
    id: 'read_only_shares',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA read-only links are available.',
  },
  remixing: {
    id: 'remixing',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA remixing is available.',
  },
  social_writes: {
    id: 'social_writes',
    availability: 'available',
    phaseGate: 'phase_7',
    reason: 'Local QA Boost and favorite emulation is available.',
  },
  camera: {
    id: 'camera',
    availability: 'unavailable',
    phaseGate: 'phase_6',
    reason: 'Camera permission is not enabled.',
  },
  microphone: {
    id: 'microphone',
    availability: 'unavailable',
    phaseGate: 'phase_6',
    reason: 'Microphone permission is not enabled.',
  },
  referrals: {
    id: 'referrals',
    availability: 'available',
    phaseGate: 'phase_7',
    reason: 'Local QA referral attribution is available.',
  },
  credits: {
    id: 'credits',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Buncha Credits are not enabled.',
  },
  iap: {
    id: 'iap',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Purchases are not enabled.',
  },
  rewarded_ads: {
    id: 'rewarded_ads',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Rewarded ads are not enabled.',
  },
} as const satisfies Record<PlatformCapabilityId, PlatformCapabilityState>;

function failure<T>(
  code: PlatformErrorCode,
  message: string,
  details?: Readonly<Record<string, unknown>>,
): PlatformResult<T> {
  return {
    ok: false,
    error: { code, message, recoverable: true, retryable: false, ...(details ? { details } : {}) },
  };
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function emptyState(): Phase4QaState {
  return {
    schemaVersion: 3,
    projects: {},
    revisions: {},
    shares: {},
    social: {},
    referrals: {},
    rideSessions: {},
  };
}

function seedFirstPartyCatalog(state: Phase4QaState): Phase4QaState {
  const seeds = [
    {
      projectId: 'seed.the-loopworks',
      publicCoasterId: 'coaster.the-loopworks',
      slug: 'the-loopworks',
      project: loopworksShowcaseFixture,
      title: 'The Loopworks',
      publishedAt: '2026-08-13T12:00:00.000Z',
      rides: 2400,
      boosts: 480,
      favorites: 310,
      remixes: 68,
    },
    {
      projectId: 'seed.first-coaster',
      publicCoasterId: 'coaster.first-coaster',
      slug: 'first-coaster',
      project: systemsCircuitCoasterFixture,
      title: 'First Coaster',
      publishedAt: '2026-07-01T12:00:00.000Z',
      rides: 1840,
      boosts: 326,
      favorites: 211,
      remixes: 47,
    },
    {
      projectId: 'seed.lift-drop',
      publicCoasterId: 'coaster.lift-drop',
      slug: 'lift-and-drop',
      project: liftDropCoasterFixture,
      title: 'Lift & Drop',
      publishedAt: '2026-07-03T12:00:00.000Z',
      rides: 1210,
      boosts: 204,
      favorites: 132,
      remixes: 31,
    },
    {
      projectId: 'seed.simple-oval',
      publicCoasterId: 'coaster.simple-oval',
      slug: 'simple-oval',
      project: ovalCoasterFixture,
      title: 'Simple Oval',
      publishedAt: '2026-07-05T12:00:00.000Z',
      rides: 860,
      boosts: 118,
      favorites: 94,
      remixes: 22,
    },
  ] as const;
  for (const seed of seeds) {
    if (state.projects[seed.projectId]) continue;
    const project = clone(seed.project);
    project.projectId = seed.projectId;
    project.revisionId = `revision.${seed.projectId}`;
    project.metadata.title = seed.title;
    project.metadata.description =
      seed.title === 'The Loopworks'
        ? loopworksShowcaseFixture.metadata.description
        : `${seed.title} is an internal Buncha Coasters QA fixture.`;
    project.metadata.creator = {
      kind: 'buncha',
      id: 'buncha.coasters',
      displayName: 'Buncha Coasters',
    };
    project.metadata.createdAt = seed.publishedAt;
    project.metadata.updatedAt = seed.publishedAt;
    project.permissions.visibility = 'public';
    project.permissions.allowRemix = true;
    const revision: StoredRevision = {
      revisionId: project.revisionId,
      projectId: project.projectId,
      project: clone(project),
      status: 'approved',
      rejectionReason: null,
      createdAt: seed.publishedAt,
      publishedAt: seed.publishedAt,
      catalogHoverUpload: null,
    };
    state.revisions[revision.revisionId] = revision;
    state.projects[project.projectId] = {
      ownerId: 'buncha.coasters',
      version: 1,
      project,
      lifecycle: 'active',
      visibility: 'public',
      workingRevisionStatus: 'approved',
      pendingRevisionId: null,
      lastRejectedRevisionId: null,
      currentPublicRevisionId: revision.revisionId,
      publicCoasterId: seed.publicCoasterId,
      publicSlug: seed.slug,
      rejectionReason: null,
      updatedAt: seed.publishedAt,
    };
    state.social[seed.publicCoasterId] = {
      boostedBy: Array.from({ length: seed.boosts }, (_, index) => `seed.boost.${index}`),
      favoritedBy: Array.from({ length: seed.favorites }, (_, index) => `seed.favorite.${index}`),
      qualifiedRides: seed.rides,
      remixes: seed.remixes,
      referredRides: 0,
      idempotency: {},
    };
  }
  return state;
}

function slugify(title: string, suffix: string): string {
  const stem =
    title
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'coaster';
  return `${stem}-${suffix
    .replace(/[^a-z0-9]/gi, '')
    .slice(-8)
    .toLowerCase()}`;
}

async function sha256(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export class LocalPhase4QaPlatformAdapter implements CoasterPlatformAdapter {
  readonly capabilities = qaStates;
  private identity: Phase4QaIdentityId;
  private readonly storage: StorageLike;
  private readonly storageKey: string;
  private readonly now: () => string;
  private readonly idFactory: (prefix: string) => string;
  private readonly catalogHoverUploads = new Map<string, CatalogHoverUploadDescriptor>();
  private activePublicCoasterId: string | null = null;

  constructor(options: LocalPhase4QaPlatformAdapterOptions) {
    this.storage = options.storage;
    this.identity = options.identity ?? 'qa.alice';
    this.storageKey = options.storageKey ?? 'buncha-coasters.phase4-qa-platform.v1';
    this.now = options.now ?? (() => new Date().toISOString());
    this.idFactory = options.idFactory ?? ((prefix) => `${prefix}.${crypto.randomUUID()}`);
  }

  setFixtureIdentity(identity: Phase4QaIdentityId): void {
    this.identity = identity;
  }

  getCapability(id: PlatformCapabilityId): PlatformCapabilityState {
    return this.capabilities[id];
  }

  async getViewer(): Promise<PlatformResult<AuthenticatedViewer>> {
    return { ok: true, value: clone(PHASE4_QA_IDENTITIES[this.identity]) };
  }

  async resolveFixtureLaunch(
    request: FixtureLaunchRequest,
  ): Promise<PlatformResult<LaunchResolution>> {
    if (!request.coasterId) return { ok: true, value: { kind: 'blank' } };
    if (request.coasterId.startsWith('fixture.'))
      return { ok: true, value: { kind: 'fixture', fixtureId: request.coasterId } };
    return failure('not_found', 'The requested fixture does not exist.');
  }

  async listCloudDrafts(): Promise<PlatformResult<readonly CloudDraftSummary[]>> {
    const state = this.read();
    const summaries = Object.values(state.projects)
      .filter((record) => record.ownerId === this.identity)
      .map((record) => ({
        projectId: record.project.projectId,
        title: record.project.metadata.title,
        version: record.version,
        state: this.ownerState(record),
        pendingRevisionId: record.pendingRevisionId,
        lastRejectedRevisionId: record.lastRejectedRevisionId,
        publicCoasterId: record.currentPublicRevisionId ? record.publicCoasterId : null,
        publicSlug: record.currentPublicRevisionId ? record.publicSlug : null,
        updatedAt: record.updatedAt,
      }))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return { ok: true, value: summaries };
  }

  async loadCloudDraft(projectId: string): Promise<PlatformResult<CloudDraftRecord>> {
    const record = this.read().projects[projectId];
    if (!record) return failure('not_found', 'Draft not found.');
    if (record.ownerId !== this.identity) return failure('not_found', 'Draft not found.');
    return { ok: true, value: this.cloudRecord(record) };
  }

  async saveCloudDraft(
    project: CoasterProject,
    options: SaveCloudDraftOptions = {},
  ): Promise<PlatformResult<CloudDraftRecord>> {
    const state = this.read();
    const existing = state.projects[project.projectId];
    if (existing && existing.ownerId !== this.identity)
      return failure('forbidden', 'Only the owner can update this draft.');
    if (existing && options.expectedVersion !== existing.version)
      return failure('conflict', 'This draft changed in another session.', {
        currentVersion: existing.version,
      });
    if (!existing && options.expectedVersion !== undefined && options.expectedVersion !== null)
      return failure('conflict', 'This draft does not exist at the expected version.', {
        currentVersion: null,
      });

    const viewer = PHASE4_QA_IDENTITIES[this.identity];
    const saved = clone(project);
    saved.metadata.creator = { kind: 'buncha', id: viewer.id, displayName: viewer.displayName };
    saved.metadata.updatedAt = this.now();
    const record: StoredProject = {
      ownerId: this.identity,
      version: (existing?.version ?? 0) + 1,
      project: saved,
      lifecycle: existing?.lifecycle ?? 'active',
      visibility: existing?.visibility ?? 'private',
      workingRevisionStatus:
        existing?.workingRevisionStatus === 'pending'
          ? 'pending'
          : existing?.currentPublicRevisionId
            ? 'approved'
            : 'not_submitted',
      pendingRevisionId: existing?.pendingRevisionId ?? null,
      lastRejectedRevisionId: existing?.lastRejectedRevisionId ?? null,
      currentPublicRevisionId: existing?.currentPublicRevisionId ?? null,
      publicCoasterId: existing?.publicCoasterId ?? null,
      publicSlug: existing?.publicSlug ?? null,
      rejectionReason: existing?.rejectionReason ?? null,
      updatedAt: saved.metadata.updatedAt,
    };
    state.projects[project.projectId] = record;
    this.write(state);
    return { ok: true, value: this.cloudRecord(record) };
  }

  async deleteCloudDraft(
    projectId: string,
    expectedVersion: number,
  ): Promise<PlatformResult<null>> {
    const state = this.read();
    const record = state.projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    if (record.version !== expectedVersion)
      return failure('conflict', 'This draft changed in another session.', {
        currentVersion: record.version,
      });
    delete state.projects[projectId];
    this.write(state);
    return { ok: true, value: null };
  }

  async requestPublish(request: PublishRequest): Promise<PlatformResult<PublishStatusRecord>> {
    const state = this.read();
    const record = state.projects[request.projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    if (record.version !== request.expectedVersion)
      return failure('conflict', 'Save the latest draft before publishing.', {
        currentVersion: record.version,
      });
    if (record.pendingRevisionId)
      return failure('conflict', 'This coaster already has a revision in review.');
    const revisionId = this.idFactory('published-revision');
    state.revisions[revisionId] = {
      revisionId,
      projectId: request.projectId,
      project: clone(record.project),
      status: 'pending',
      rejectionReason: null,
      createdAt: this.now(),
      publishedAt: null,
      catalogHoverUpload: request.catalogHoverUpload ?? null,
    };
    record.pendingRevisionId = revisionId;
    record.workingRevisionStatus = 'pending';
    record.rejectionReason = null;
    record.updatedAt = this.now();
    this.write(state);
    return { ok: true, value: this.publishStatus(record) };
  }

  async moderatePending(
    projectId: string,
    decision: { kind: 'approve' } | { kind: 'reject'; reason: string },
  ): Promise<PlatformResult<PublishStatusRecord>> {
    const state = this.read();
    const record = state.projects[projectId];
    if (!record?.pendingRevisionId) return failure('not_found', 'No pending revision exists.');
    const revision = state.revisions[record.pendingRevisionId];
    if (!revision) return failure('platform_error', 'The pending revision is missing.');
    if (decision.kind === 'approve') {
      revision.status = 'approved';
      revision.publishedAt = this.now();
      record.currentPublicRevisionId = revision.revisionId;
      record.publicCoasterId ??= this.idFactory('public-coaster');
      record.publicSlug ??= slugify(revision.project.metadata.title, record.publicCoasterId);
      record.visibility = 'public';
      record.workingRevisionStatus = 'approved';
      record.rejectionReason = null;
    } else {
      revision.status = 'rejected';
      revision.rejectionReason = decision.reason;
      record.workingRevisionStatus = 'rejected';
      record.lastRejectedRevisionId = revision.revisionId;
      record.rejectionReason = decision.reason;
    }
    record.pendingRevisionId = null;
    record.updatedAt = this.now();
    this.write(state);
    return { ok: true, value: this.publishStatus(record) };
  }

  async getPublishStatus(projectId: string): Promise<PlatformResult<PublishStatusRecord>> {
    const record = this.read().projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    return { ok: true, value: this.publishStatus(record) };
  }

  async requestHeroUpload(projectId: string): Promise<PlatformResult<HeroUploadDescriptor>> {
    const record = this.read().projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    const expiresAt = new Date(Date.parse(this.now()) + 10 * 60_000).toISOString();
    const asset = <W extends number, H extends number>(role: string, width: W, height: H) => ({
      uploadId: this.idFactory(`hero-${role}`),
      objectKey: `qa/coasters/${projectId}/${role}`,
      uploadUrl: `local-qa://hero/${projectId}/${role}`,
      acceptedContentTypes: ['image/webp', 'image/png'] as const,
      maxBytes: 2_000_000,
      requiredWidth: width,
      requiredHeight: height,
    });
    return {
      ok: true,
      value: {
        expiresAt,
        assets: {
          hero16x9: asset('hero16x9', 1280, 720),
          openGraph: asset('openGraph', 1200, 630),
        },
      },
    };
  }

  async uploadHeroAssets(
    projectId: string,
    assets: HeroAssetBundle,
  ): Promise<PlatformResult<HeroUploadDescriptor>> {
    const descriptor = await this.requestHeroUpload(projectId);
    if (!descriptor.ok) return descriptor;
    const pairs = [
      [descriptor.value.assets.hero16x9, assets.hero16x9],
      [descriptor.value.assets.openGraph, assets.openGraph],
    ] as const;
    for (const [target, blob] of pairs) {
      if (!target.acceptedContentTypes.includes(blob.type as 'image/webp' | 'image/png'))
        return failure('validation_failed', 'Hero images must be WebP or PNG files.');
      if (blob.size > target.maxBytes)
        return failure('validation_failed', 'A hero image exceeds the upload size limit.');
    }
    return descriptor;
  }

  async uploadCatalogHoverVideo(
    projectId: string,
    asset: CatalogHoverVideoAsset,
    idempotencyKey: string,
  ): Promise<PlatformResult<CatalogHoverUploadDescriptor>> {
    const prior = this.catalogHoverUploads.get(idempotencyKey);
    if (prior) return { ok: true, value: clone(prior) };
    const record = this.read().projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    const contentType = asset.blob.type.split(';')[0];
    if (contentType !== 'video/webm' && contentType !== 'video/mp4')
      return failure('validation_failed', 'The Buncha Games motion preview must be WebM or MP4.');
    const validDimensions =
      (asset.width === 640 && asset.height === 360) ||
      (asset.width === 480 && asset.height === 270);
    if (!validDimensions)
      return failure(
        'validation_failed',
        'The Buncha Games motion preview must be 640×360 or 480×270.',
      );
    if (asset.durationSeconds < 7.9 || asset.durationSeconds > 8.1)
      return failure('validation_failed', 'The Buncha Games motion preview must be eight seconds.');
    if (asset.blob.size > 1_500_000)
      return failure(
        'validation_failed',
        'The Buncha Games motion preview exceeds the 1.5 MB limit.',
      );
    const descriptor: CatalogHoverUploadDescriptor = {
      uploadId: this.idFactory('catalog-hover'),
      objectKey: `qa/coasters/${projectId}/catalog-hover`,
      uploadUrl: `local-qa://catalog-hover/${projectId}`,
      contentType,
      width: asset.width,
      height: asset.height,
      durationSeconds: asset.durationSeconds,
      byteSize: asset.blob.size,
      muted: true,
      visualFingerprint: asset.visualFingerprint,
      idempotencyKey,
    };
    this.catalogHoverUploads.set(idempotencyKey, descriptor);
    return { ok: true, value: clone(descriptor) };
  }

  async getPublicCoaster(reference: {
    publicCoasterId?: string;
    publicSlug?: string;
  }): Promise<PlatformResult<PublicCoasterRecord>> {
    const state = this.read();
    const record = Object.values(state.projects).find(
      (candidate) =>
        (reference.publicCoasterId && candidate.publicCoasterId === reference.publicCoasterId) ||
        (reference.publicSlug && candidate.publicSlug === reference.publicSlug),
    );
    if (!record?.currentPublicRevisionId || !record.publicCoasterId || !record.publicSlug)
      return failure('not_found', 'Public coaster not found.');
    const revision = state.revisions[record.currentPublicRevisionId];
    if (!this.isPublic(record) || !revision || revision.status !== 'approved')
      return failure('not_found', 'Public coaster not found.');
    this.activePublicCoasterId = record.publicCoasterId;
    return { ok: true, value: this.publicRecord(record, revision) };
  }

  async listPublicCoasters(query: PublicCatalogQuery): Promise<PlatformResult<PublicCatalogPage>> {
    const state = this.read();
    const limit = Math.min(Math.max(query.limit ?? 18, 1), 18);
    const offset = this.decodeCursor(query.cursor, query.sort);
    const cards = Object.values(state.projects)
      .filter(
        (record) =>
          this.isPublic(record) &&
          record.currentPublicRevisionId &&
          record.publicCoasterId &&
          (!query.remixableOnly || record.project.permissions.allowRemix) &&
          (!query.creatorId || this.publicCreatorId(record.ownerId) === query.creatorId),
      )
      .map((record) => this.publicCard(state, record))
      .filter((card): card is PublicCoasterCardData => Boolean(card));
    cards.sort((a, b) => {
      if (query.sort === 'newest') return b.publishedAt.localeCompare(a.publishedAt);
      const primary =
        query.sort === 'most_ridden'
          ? b.stats.qualifiedRides - a.stats.qualifiedRides
          : b.stats.boosts - a.stats.boosts;
      if (primary !== 0) return primary;
      const secondary =
        query.sort === 'most_ridden'
          ? b.stats.boosts - a.stats.boosts
          : b.stats.qualifiedRides - a.stats.qualifiedRides;
      return secondary || b.publishedAt.localeCompare(a.publishedAt);
    });
    const pageItems = cards.slice(offset, offset + limit);
    return {
      ok: true,
      value: {
        items: pageItems,
        nextCursor:
          offset + limit < cards.length ? this.encodeCursor(offset + limit, query.sort) : null,
        sort: query.sort,
        rankingWindowLabel: query.sort === 'newest' ? null : 'Past 30 days',
        generatedAt: this.now(),
      },
    };
  }

  async getViewerCoasterStates(
    publicCoasterIds: readonly string[],
  ): Promise<PlatformResult<PublicCoasterViewerStateBatch>> {
    if (publicCoasterIds.length > 50)
      return failure('invalid_request', 'Viewer state is limited to 50 coasters.');
    const state = this.read();
    const items: Record<string, { boosted: boolean; favorited: boolean; isOwner: boolean }> = {};
    for (const publicCoasterId of publicCoasterIds) {
      const record = Object.values(state.projects).find(
        (candidate) => this.isPublic(candidate) && candidate.publicCoasterId === publicCoasterId,
      );
      if (!record) continue;
      const social = this.ensureSocial(state, publicCoasterId);
      items[publicCoasterId] = {
        boosted: social.boostedBy.includes(this.identity),
        favorited: social.favoritedBy.includes(this.identity),
        isOwner: record.ownerId === this.identity,
      };
    }
    return { ok: true, value: { items } };
  }

  async listFavoriteCoasters(
    query: PublicCatalogQuery,
  ): Promise<PlatformResult<PublicCatalogPage>> {
    const result = await this.listPublicCoasters({ ...query, cursor: undefined, limit: 18 });
    if (!result.ok) return result;
    const state = this.read();
    const items = result.value.items.filter((card) =>
      state.social[card.publicCoasterId]?.favoritedBy.includes(this.identity),
    );
    return { ok: true, value: { ...result.value, items, nextCursor: null } };
  }

  async listMyCoasters(): Promise<PlatformResult<readonly MyCoasterSummary[]>> {
    const state = this.read();
    const items = Object.values(state.projects)
      .filter(
        (record) =>
          record.ownerId === this.identity &&
          record.lifecycle === 'active' &&
          (record.currentPublicRevisionId ||
            record.pendingRevisionId ||
            record.visibility === 'hidden'),
      )
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .map((record) => ({
        projectId: record.project.projectId,
        title: record.project.metadata.title,
        version: record.version,
        state: this.ownerState(record),
        pendingRevisionId: record.pendingRevisionId,
        lastRejectedRevisionId: record.lastRejectedRevisionId,
        publicCoasterId: record.currentPublicRevisionId ? record.publicCoasterId : null,
        publicSlug: record.currentPublicRevisionId ? record.publicSlug : null,
        updatedAt: record.updatedAt,
        heroUrl: null,
        intensityLabel: record.currentPublicRevisionId ? 'Intense' : null,
        stats: record.publicCoasterId ? this.socialStats(state, record.publicCoasterId) : null,
      }));
    return { ok: true, value: items };
  }

  async setBoost(request: SocialMutationRequest): Promise<PlatformResult<SocialMutationResult>> {
    return this.setSocialState(request, 'boost');
  }

  async setFavorite(request: SocialMutationRequest): Promise<PlatformResult<SocialMutationResult>> {
    return this.setSocialState(request, 'favorite');
  }

  async createReferralLink(publicCoasterId: string): Promise<PlatformResult<ReferralLinkResult>> {
    const state = this.read();
    const target = Object.values(state.projects).find(
      (record) => this.isPublic(record) && record.publicCoasterId === publicCoasterId,
    );
    if (!target?.publicSlug) return failure('not_found', 'Public coaster not found.');
    const referralId = this.idFactory('ref');
    const createdAt = this.now();
    const expiresAt = new Date(Date.parse(createdAt) + 90 * 24 * 60 * 60_000).toISOString();
    state.referrals[referralId] = {
      publicCoasterId,
      createdBy: this.identity,
      createdAt,
      expiresAt,
      completedRideSessions: [],
    };
    this.write(state);
    return {
      ok: true,
      value: {
        referralId,
        canonicalPath: `/coasters/${encodeURIComponent(target.publicSlug)}?ref=${encodeURIComponent(referralId)}`,
        expiresAt,
      },
    };
  }

  async startQualifiedRide(
    request: QualifiedRideStartRequest,
  ): Promise<PlatformResult<QualifiedRideStartResult>> {
    if (!this.activePublicCoasterId) return unavailable(this.capabilities.referrals);
    const state = this.read();
    const existing = Object.entries(state.rideSessions).find(
      ([, session]) =>
        session.publicCoasterId === this.activePublicCoasterId && session.completedAt === null,
    );
    if (existing)
      return { ok: true, value: { rideSessionId: existing[0], startedAt: existing[1].startedAt } };
    const rideSessionId = this.idFactory('ride');
    const startedAt = this.now();
    const referral = request.referralId ? state.referrals[request.referralId] : null;
    state.rideSessions[rideSessionId] = {
      publicCoasterId: this.activePublicCoasterId,
      referralId:
        referral &&
        referral.publicCoasterId === this.activePublicCoasterId &&
        Date.parse(referral.expiresAt) > Date.parse(startedAt)
          ? request.referralId!
          : null,
      startedAt,
      completedAt: null,
      qualified: false,
      counted: false,
    };
    this.write(state);
    return { ok: true, value: { rideSessionId, startedAt } };
  }

  async completeQualifiedRide(
    request: QualifiedRideCompletionRequest,
  ): Promise<PlatformResult<QualifiedRideCompletionResult>> {
    const state = this.read();
    const session = state.rideSessions[request.rideSessionId];
    if (!session) return failure('not_found', 'Ride session not found.');
    if (session.completedAt)
      return {
        ok: true,
        value: {
          qualified: session.qualified,
          counted: session.counted,
          completedAt: session.completedAt,
        },
      };
    const completedAt = this.now();
    const qualified = Date.parse(completedAt) - Date.parse(session.startedAt) >= 15_000;
    session.completedAt = completedAt;
    session.qualified = qualified;
    session.counted = qualified;
    if (qualified) {
      const social = this.ensureSocial(state, session.publicCoasterId);
      social.qualifiedRides += 1;
      if (session.referralId) {
        const referral = state.referrals[session.referralId];
        if (referral && !referral.completedRideSessions.includes(request.rideSessionId)) {
          referral.completedRideSessions.push(request.rideSessionId);
          social.referredRides += 1;
        }
      }
    }
    this.write(state);
    return { ok: true, value: { qualified, counted: qualified, completedAt } };
  }

  async createReadOnlyShare(projectId: string): Promise<PlatformResult<ReadOnlyShare>> {
    const state = this.read();
    const record = state.projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    const rideFingerprint = await createRideCriticalFingerprint(record.project);
    if (!isSuccessfulTestCurrent(record.project.successfulTest, rideFingerprint))
      return failure('conflict', 'Complete a Test Ride for this coaster before sharing it.');
    const shareId = this.idFactory('share');
    const secret = this.idFactory('capability');
    const createdAt = this.now();
    state.shares[shareId] = {
      shareId,
      ownerId: this.identity,
      projectId,
      revisionId: record.project.revisionId,
      secretHash: await sha256(secret),
      project: clone(record.project),
      createdAt,
    };
    this.write(state);
    return {
      ok: true,
      value: {
        shareId,
        url: `/coasters/share/${shareId}#cap=${encodeURIComponent(secret)}`,
        projectId,
        revisionId: record.project.revisionId,
        createdAt,
        expiresAt: new Date(Date.parse(createdAt) + 30 * 24 * 60 * 60 * 1_000).toISOString(),
      },
    };
  }

  async revokeReadOnlyShare(shareId: string): Promise<PlatformResult<null>> {
    const state = this.read();
    const share = state.shares[shareId];
    if (!share || share.ownerId !== this.identity)
      return failure('not_found', 'Share link not found.');
    delete state.shares[shareId];
    this.write(state);
    return { ok: true, value: null };
  }

  async loadReadOnlyShare(
    shareId: string,
    secret: string,
  ): Promise<PlatformResult<ReadOnlyShareRecord>> {
    const share = this.read().shares[shareId];
    if (!share || share.secretHash !== (await sha256(secret)))
      return failure('not_found', 'Share link not found.');
    return {
      ok: true,
      value: {
        shareId,
        projectId: share.projectId,
        revisionId: share.revisionId,
        title: share.project.metadata.title,
        project: clone(share.project),
        allowRemix: false,
      },
    };
  }

  async resolvePrivateDraftLaunch(): Promise<PlatformResult<ReadOnlyShareRecord>> {
    return failure('platform_error', 'Private hosted launch is unavailable in Local QA.');
  }

  async remixCoaster(request: RemixRequest): Promise<PlatformResult<RemixResult>> {
    let source: CoasterProject | null = null;
    let sourceRevisionId = '';
    if (request.publicCoasterId) {
      const state = this.read();
      const sourceRecord = Object.values(state.projects).find(
        (record) => this.isPublic(record) && record.publicCoasterId === request.publicCoasterId,
      );
      const revision = sourceRecord?.currentPublicRevisionId
        ? state.revisions[sourceRecord.currentPublicRevisionId]
        : null;
      if (!sourceRecord || !revision || revision.status !== 'approved')
        return failure('not_found', 'Public coaster not found.');
      source = clone(revision.project);
      sourceRevisionId = revision.revisionId;
    } else if (request.share)
      return failure('forbidden', 'Private Coaster Ride links cannot be remixed.');
    if (!source) return failure('invalid_request', 'Choose a public coaster to remix.');
    if (!source.permissions.allowRemix)
      return failure('forbidden', 'The creator has not enabled remixing.');

    const now = this.now();
    const sourceProjectId = source.projectId;
    const remix = clone(source);
    remix.projectId = this.idFactory('project');
    remix.revisionId = this.idFactory('revision');
    remix.metadata.title = `${source.metadata.title} Remix`.slice(0, 80);
    remix.metadata.createdAt = now;
    remix.metadata.updatedAt = now;
    const viewer = PHASE4_QA_IDENTITIES[this.identity];
    remix.metadata.creator = { kind: 'buncha', id: viewer.id, displayName: viewer.displayName };
    remix.permissions.visibility = 'private';
    remix.permissions.allowReadOnlyShare = false;
    remix.validation = createUnvalidatedValidationSummary();
    remix.lineage = {
      sourceProjectId,
      sourceRevisionId,
      rootProjectId: source.lineage?.rootProjectId ?? sourceProjectId,
      remixedAt: now,
    };
    const saved = await this.saveCloudDraft(remix);
    if (!saved.ok) return saved;
    if (request.publicCoasterId) {
      const state = this.read();
      this.ensureSocial(state, request.publicCoasterId).remixes += 1;
      this.write(state);
    }
    return { ok: true, value: { draft: saved.value, lineage: clone(remix.lineage) } };
  }

  async publish(projectId: string, _revisionId: string): Promise<PlatformResult<never>> {
    const record = this.read().projects[projectId];
    if (!record) return failure('not_found', 'Draft not found.');
    const result = await this.requestPublish({
      projectId,
      expectedVersion: record.version,
      idempotencyKey: `local-publish-${record.version}`,
    });
    return result.ok ? { ok: true, value: undefined as never } : result;
  }

  async requestCamera(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.camera);
  }
  async requestMicrophone(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.microphone);
  }
  async attributeReferral(_token: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.referrals);
  }
  async getCreditBalance(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.credits);
  }
  async purchase(_sku: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.iap);
  }
  async showRewardedAd(_placement: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.rewarded_ads);
  }

  private ensureSocial(
    state: Phase4QaState,
    publicCoasterId: string,
  ): Phase4QaState['social'][string] {
    return (state.social[publicCoasterId] ??= {
      boostedBy: [],
      favoritedBy: [],
      qualifiedRides: 0,
      remixes: 0,
      referredRides: 0,
      idempotency: {},
    });
  }

  private socialStats(state: Phase4QaState, publicCoasterId: string): PublicCoasterStats {
    const social = this.ensureSocial(state, publicCoasterId);
    return {
      qualifiedRides: social.qualifiedRides,
      boosts: social.boostedBy.length,
      remixes: social.remixes,
      favorites: social.favoritedBy.length,
    };
  }

  private publicCard(state: Phase4QaState, record: StoredProject): PublicCoasterCardData | null {
    if (!record.currentPublicRevisionId || !record.publicCoasterId || !record.publicSlug)
      return null;
    const revision = state.revisions[record.currentPublicRevisionId];
    if (
      !this.isPublic(record) ||
      !revision ||
      revision.status !== 'approved' ||
      !revision.publishedAt
    )
      return null;
    const project = revision.project;
    const sourceRecord = project.lineage
      ? Object.values(state.projects).find(
          (candidate) => candidate.project.projectId === project.lineage?.sourceProjectId,
        )
      : null;
    const intensity = record.publicCoasterId.includes('lift-drop')
      ? { level: 'thrilling' as const, label: 'Thrilling' }
      : { level: 'intense' as const, label: 'Intense' };
    return {
      publicCoasterId: record.publicCoasterId,
      publicSlug: record.publicSlug,
      title: project.metadata.title,
      description: project.metadata.description,
      creator: {
        publicCreatorId: this.publicCreatorId(record.ownerId),
        displayName: project.metadata.creator.displayName,
        avatarUrl: null,
        firstParty: record.ownerId === 'buncha.coasters',
        deleted: false,
      },
      themeId: project.appearance.environmentThemeId,
      heroUrl: null,
      allowRemix: project.permissions.allowRemix,
      publishedAt: revision.publishedAt,
      intensity,
      lineage: project.lineage
        ? {
            sourcePublicCoasterId: sourceRecord?.publicCoasterId ?? null,
            sourceTitle: sourceRecord?.project.metadata.title ?? null,
            rootPublicCoasterId:
              Object.values(state.projects).find(
                (candidate) => candidate.project.projectId === project.lineage?.rootProjectId,
              )?.publicCoasterId ?? null,
          }
        : null,
      stats: this.socialStats(state, record.publicCoasterId),
    };
  }

  private setSocialState(
    request: SocialMutationRequest,
    kind: 'boost' | 'favorite',
  ): PlatformResult<SocialMutationResult> {
    const state = this.read();
    const target = Object.values(state.projects).find(
      (record) => this.isPublic(record) && record.publicCoasterId === request.publicCoasterId,
    );
    if (!target) return failure('not_found', 'Public coaster not found.');
    if (target.ownerId === this.identity)
      return failure('forbidden', `You cannot ${kind} your own coaster.`);
    const social = this.ensureSocial(state, request.publicCoasterId);
    const collection = kind === 'boost' ? social.boostedBy : social.favoritedBy;
    if (!social.idempotency[request.idempotencyKey]) {
      const currentIndex = collection.indexOf(this.identity);
      if (request.desired && currentIndex < 0) collection.push(this.identity);
      if (!request.desired && currentIndex >= 0) collection.splice(currentIndex, 1);
      social.idempotency[request.idempotencyKey] = true;
      this.write(state);
    }
    return {
      ok: true,
      value: {
        publicCoasterId: request.publicCoasterId,
        boosted: social.boostedBy.includes(this.identity),
        favorited: social.favoritedBy.includes(this.identity),
        authoritativeCounts: this.socialStats(state, request.publicCoasterId),
        newMilestones: [],
      },
    };
  }

  private read(): Phase4QaState {
    const raw = this.storage.getItem(this.storageKey);
    if (!raw) {
      const seeded = seedFirstPartyCatalog(emptyState());
      this.write(seeded);
      return seeded;
    }
    try {
      const parsed = JSON.parse(raw) as Omit<Phase4QaState, 'schemaVersion'> & {
        schemaVersion: number;
      };
      if (![1, 2, 3].includes(parsed.schemaVersion)) return emptyState();
      const migrated: Phase4QaState = {
        schemaVersion: 3,
        projects: Object.fromEntries(
          Object.entries(parsed.projects ?? {}).map(([projectId, raw]) => {
            const record = raw as StoredProject & { status?: string };
            if (record.lifecycle) return [projectId, record];
            const legacy = record.status ?? 'draft_private';
            return [
              projectId,
              {
                ...record,
                lifecycle: legacy === 'archived' ? 'archived' : 'active',
                visibility:
                  legacy === 'public' ? 'public' : legacy === 'hidden' ? 'hidden' : 'private',
                workingRevisionStatus:
                  legacy === 'pending_review'
                    ? 'pending'
                    : legacy === 'rejected'
                      ? 'rejected'
                      : record.currentPublicRevisionId
                        ? 'approved'
                        : 'not_submitted',
                lastRejectedRevisionId: null,
                status: undefined,
              },
            ];
          }),
        ),
        revisions: parsed.revisions ?? {},
        shares: parsed.shares ?? {},
        social: parsed.social ?? {},
        referrals: parsed.referrals ?? {},
        rideSessions: parsed.rideSessions ?? {},
      };
      for (const revision of Object.values(migrated.revisions)) {
        if ((revision.status as string) === 'pending_review') revision.status = 'pending';
        if ((revision.status as string) === 'public') revision.status = 'approved';
      }
      const neededSeed = !migrated.projects['seed.first-coaster'];
      seedFirstPartyCatalog(migrated);
      if (parsed.schemaVersion !== 3 || neededSeed) this.write(migrated);
      return migrated;
    } catch {
      return seedFirstPartyCatalog(emptyState());
    }
  }

  private write(state: Phase4QaState): void {
    this.storage.setItem(this.storageKey, JSON.stringify(state));
  }

  private cloudRecord(record: StoredProject): CloudDraftRecord {
    return {
      projectId: record.project.projectId,
      ownerId: record.ownerId,
      version: record.version,
      project: clone(record.project),
      state: this.ownerState(record),
      pendingRevisionId: record.pendingRevisionId,
      lastRejectedRevisionId: record.lastRejectedRevisionId,
      currentPublicRevisionId: record.currentPublicRevisionId,
      publicCoasterId: record.currentPublicRevisionId ? record.publicCoasterId : null,
      publicSlug: record.currentPublicRevisionId ? record.publicSlug : null,
      updatedAt: record.updatedAt,
    };
  }

  private publishStatus(record: StoredProject): PublishStatusRecord {
    return {
      projectId: record.project.projectId,
      state: this.ownerState(record),
      pendingRevisionId: record.pendingRevisionId,
      currentPublicRevisionId: record.currentPublicRevisionId,
      publicCoasterId: record.currentPublicRevisionId ? record.publicCoasterId : null,
      publicSlug: record.currentPublicRevisionId ? record.publicSlug : null,
      rejectionReason: record.rejectionReason,
      updatedAt: record.updatedAt,
    };
  }

  private publicRecord(record: StoredProject, revision: StoredRevision): PublicCoasterRecord {
    const project = clone(revision.project);
    const card = this.publicCard(this.read(), record);
    if (!card) throw new Error('Public QA coaster is unavailable.');
    const definition = clone(project) as PublicCoasterPayload['definition'];
    delete (definition as Partial<CoasterProject>).projectId;
    delete (definition as Partial<CoasterProject>).revisionId;
    delete (definition as Partial<CoasterProject>).lineage;
    definition.metadata = {
      title: project.metadata.title,
      description: project.metadata.description,
      createdAt: project.metadata.createdAt,
      updatedAt: project.metadata.updatedAt,
      tags: [...project.metadata.tags],
    };
    definition.permissions = { allowRemix: project.permissions.allowRemix };
    return hydratePublicCoaster({
      ...card,
      immutableRevisionId: revision.revisionId,
      contentHash: `qa.${record.publicCoasterId}`,
      definition,
    });
  }

  private ownerState(record: StoredProject): OwnerProjectState {
    return {
      lifecycle: record.lifecycle,
      visibility: record.visibility,
      workingRevisionStatus: record.workingRevisionStatus,
    };
  }

  private isPublic(record: StoredProject): boolean {
    return (
      record.lifecycle === 'active' &&
      record.visibility === 'public' &&
      Boolean(record.currentPublicRevisionId)
    );
  }

  private publicCreatorId(ownerId: string): string {
    if (ownerId === 'buncha.coasters') return 'creator.buncha-coasters';
    return ownerId === 'qa.alice' ? 'creator.qa-alice' : 'creator.qa-bob';
  }

  private encodeCursor(offset: number, sort: PublicCatalogQuery['sort']): string {
    return btoa(JSON.stringify({ v: 1, sort, offset }));
  }

  private decodeCursor(cursor: string | undefined, sort: PublicCatalogQuery['sort']): number {
    if (!cursor) return 0;
    try {
      const parsed = JSON.parse(atob(cursor)) as { v?: number; sort?: string; offset?: number };
      return parsed.v === 1 && parsed.sort === sort && Number.isInteger(parsed.offset)
        ? Math.max(parsed.offset ?? 0, 0)
        : 0;
    } catch {
      return 0;
    }
  }
}

export function createLocalPhase4QaPlatformAdapter(
  options: LocalPhase4QaPlatformAdapterOptions,
): LocalPhase4QaPlatformAdapter {
  return new LocalPhase4QaPlatformAdapter(options);
}
